import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Salad, LineChart, MessageSquare, CreditCard, ArrowRight, CheckCircle2, Circle, Youtube, Trophy, Clock } from "lucide-react";
import AppShell from "../../components/AppShell";
import SessionView from "../../components/SessionView";
import ReadinessCard from "../../components/ReadinessCard";
import TeamLogo from "../../components/TeamLogo";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { DAY_TYPES, BLOCK_TYPES, monthKey, ymd, SPANISH_DOW } from "../../lib/training";

const HERO_BG = "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/z57p91jo_IMG_2623%202.PNG";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [tests, setTests] = useState([]);
  const [lastFeedback, setLastFeedback] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const now = new Date();
        const [a, b, c] = await Promise.all([
          api.get("/me/sessions", { params: { month: monthKey(now) } }),
          api.get("/me/tests"),
          api.get("/me/feedback"),
        ]);
        setSessions(a.data);
        setTests(b.data);
        setLastFeedback((c.data && c.data[0]) || null);
      } catch { /* silent */ }
    })();
  }, []);

  const today = ymd(new Date());
  const todaySession = sessions.find((s) => s.date === today) || null;

  // Week strip: 7 days starting Monday of current week
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
  const byDate = sessions.reduce((m, s) => ((m[s.date] = s), m), {});

  // Next match
  const nextMatch = sessions
    .filter((s) => (s.day_type === "partido" || s.day_type === "partido_suplente") && s.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))[0];

  const renews = user?.subscription_renews_at ? new Date(user.subscription_renews_at) : null;
  const subActive = user?.subscription_status === "active";

  // Si el cliente subió foto propia, la usamos como hero. Si no, cae al fondo por defecto.
  const heroBg = user?.avatar_url || HERO_BG;
  const heroPosition = user?.avatar_url ? "center top" : "center";

  return (
    <AppShell>
      {/* Hero strip */}
      <div style={{
        position: "relative", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)",
        padding: "28px 22px", marginBottom: 22, background: "#0a0a0a",
      }} data-testid="client-dashboard">
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: "55%",
          backgroundImage: `linear-gradient(to right, #0a0a0a 0%, rgba(10,10,10,0.4) 50%, transparent 100%), url(${heroBg})`,
          backgroundSize: "cover", backgroundPosition: heroPosition,
        }} data-testid="client-hero-image" />
        <div style={{ position: "relative", maxWidth: 600 }}>
          <div className="qv-overline">Bienvenido, {user?.name?.split(" ")[0]}</div>
          <h1 className="qv-display" style={{ fontSize: "clamp(2.2rem, 5vw, 3.4rem)", marginTop: 6 }}>
            ENTRENA.<br/>RECUPERA.<br/><span style={{ color: "var(--qv-red)" }}>RINDE.</span>
          </h1>
        </div>
      </div>

      {/* Subscription banner */}
      {!subActive && (
        <div className="qv-card-app" style={{ marginBottom: 18, borderColor: "rgba(225,29,46,0.4)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div className="qv-overline">{user?.subscription_status === "trial" ? "Modo prueba" : "Suscripción inactiva"}</div>
              <div style={{ fontFamily: "Bebas Neue", fontSize: "1.5rem", marginTop: 4 }}>Activa tu plan mensual</div>
              <div style={{ color: "#a3a3a3", fontSize: 12, marginTop: 4 }}>Renueva tu acceso por {user?.monthly_price_eur || 50}€/mes.</div>
            </div>
            <Link to="/app/ajustes" className="qv-btn-primary" data-testid="cta-subscribe">
              <CreditCard size={14} /> SUSCRIBIRME
            </Link>
          </div>
        </div>
      )}
      {subActive && renews && (
        <div style={{ marginBottom: 18, padding: 12, background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.25)", fontSize: 11, color: "#a3a3a3", letterSpacing: "0.18em", textTransform: "uppercase" }}>
          Suscripción activa · renueva el {renews.toLocaleDateString("es-ES")}
        </div>
      )}

      {/* Next match banner */}
      {nextMatch && (
        <div className="qv-card-app" style={{ marginBottom: 18, borderColor: "rgba(225,29,46,0.45)", background: "rgba(225,29,46,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            {nextMatch.match?.rival_logo_url ? (
              <TeamLogo src={nextMatch.match.rival_logo_url} alt={nextMatch.match.rival} size={48} bg="transparent" />
            ) : (
              <div style={{ width: 48, height: 48, background: "rgba(225,29,46,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Trophy size={22} color="var(--qv-red)" />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div className="qv-overline">Próximo partido</div>
              <div style={{ fontFamily: "Bebas Neue", fontSize: "1.3rem" }}>vs {nextMatch.match?.rival || "Rival"}</div>
              <div style={{ display: "flex", gap: 10, color: "#a3a3a3", fontSize: 11, marginTop: 2 }}>
                <span>{new Date(nextMatch.date + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" })}</span>
                {nextMatch.match?.kickoff_time && <span><Clock size={10} style={{ display: "inline", marginRight: 3 }} />{nextMatch.match.kickoff_time}</span>}
              </div>
            </div>
            <button onClick={() => setViewing(nextMatch)} className="qv-btn-ghost" style={{ fontSize: 10 }}>VER</button>
          </div>
        </div>
      )}

      {/* Weekly mini-strip */}
      <div className="qv-card-app" style={{ marginBottom: 18 }}>
        <div className="qv-card-head">
          <h3>Tu semana</h3>
          <Link to="/app/entrenamiento" className="qv-btn-ghost" style={{ fontSize: 10 }}>VER MES →</Link>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {weekDays.map((d, i) => {
            const ds = ymd(d);
            const s = byDate[ds];
            const dt = s ? DAY_TYPES[s.day_type] : null;
            const isToday = ds === today;
            return (
              <button
                key={ds}
                onClick={() => s && setViewing(s)}
                style={{
                  background: dt ? dt.bg : "#070707",
                  border: `1.5px solid ${dt ? dt.border : (isToday ? "rgba(225,29,46,0.7)" : "rgba(255,255,255,0.06)")}`,
                  padding: "10px 4px",
                  cursor: s ? "pointer" : "default",
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
                data-testid={`week-${ds}`}
              >
                <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.2em", fontWeight: 700 }}>{SPANISH_DOW[i]}</div>
                <div style={{ fontFamily: "Bebas Neue", fontSize: "1.2rem", color: isToday ? "var(--qv-red)" : "#fff" }}>{d.getDate()}</div>
                {dt && <dt.icon size={12} color={dt.color} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Today */}
      <div className="qv-card-app" style={{ marginBottom: 18 }}>
        <div className="qv-card-head">
          <h3>Hoy</h3>
          {todaySession && <button onClick={() => setViewing(todaySession)} className="qv-btn-primary" style={{ fontSize: 10 }} data-testid="open-today">ABRIR SESIÓN <ArrowRight size={12} /></button>}
        </div>
        {!todaySession ? (
          <div className="qv-empty" style={{ padding: 28 }}>Hoy no tienes nada programado.</div>
        ) : (
          <TodaySummary session={todaySession} />
        )}
      </div>

      {/* Readiness */}
      <div style={{ marginBottom: 18 }}>
        <ReadinessCard feedback={lastFeedback} />
      </div>

      {/* Quick access */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {[
          { to: "/app/entrenamiento", icon: Activity, lbl: "Entrenamiento" },
          { to: "/app/nutricion", icon: Salad, lbl: "Nutrición" },
          { to: "/app/tests", icon: LineChart, lbl: "Tests" },
          { to: "/app/feedback", icon: MessageSquare, lbl: "Feedback" },
        ].map((c) => (
          <Link key={c.to} to={c.to} className="qv-card-app" style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", gap: 6, padding: 16 }}>
            <c.icon size={22} color="var(--qv-red)" />
            <div style={{ fontFamily: "Bebas Neue", fontSize: "1.2rem", marginTop: 2 }}>{c.lbl.toUpperCase()}</div>
          </Link>
        ))}
      </div>

      {viewing && (
        <SessionView
          session={viewing}
          onClose={() => setViewing(null)}
          onUpdated={(u) => {
            setSessions((arr) => arr.map((s) => (s.id === u.id ? u : s)));
            setViewing(u);
          }}
        />
      )}
    </AppShell>
  );
}

function TodaySummary({ session }) {
  const dt = DAY_TYPES[session.day_type] || DAY_TYPES.fuerza;
  const completed = new Set(session.completed_exercise_ids || []);
  const total = (session.blocks || []).reduce((n, b) => n + (b.exercises || []).length, 0);
  const done = (session.blocks || []).reduce((n, b) => n + (b.exercises || []).filter((e) => completed.has(e.id)).length, 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: dt.color, padding: "5px 10px", border: `1px solid ${dt.border}`, background: dt.bg, fontSize: 10, letterSpacing: "0.22em", fontWeight: 700, textTransform: "uppercase" }}>
          <dt.icon size={12} /> {dt.label}
        </span>
        {session.title && <span style={{ fontSize: 13, color: "#cfcfcf" }}>{session.title}</span>}
      </div>
      {total > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#a3a3a3", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>
            <span>PROGRESO</span><span>{done}/{total}</span>
          </div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: dt.color }} />
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {(session.blocks || []).slice(0, 3).map((b) => {
          const bt = BLOCK_TYPES[b.type] || BLOCK_TYPES.fuerza;
          return (
            <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, color: "#a3a3a3", fontSize: 12 }}>
              <bt.icon size={13} color={bt.color} />
              <span>{bt.label}</span>
              <span style={{ color: "#737373" }}>· {b.exercises.length} ejercicios</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
